import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { FacetsComponent } from '@b2b/components/facets/facets.component';
import { LocalLoaderComponent } from '@shared/components/loader/local-loader/local-loader.component';
import { ProductCardComponent } from '@b2b/components/product/product-card/product-card.component';
import { CmsComponent, CX_COMPONENT, Link } from '@app/core/models/atomics.model';
import { BreadcrumbService } from '@app/core/services/breadcrumb.service';
import { ColumnsConfigComponent } from '@b2b/features/product/components/columns-config/columns-config.component';
import { ProductsTableComponent } from '@b2b/features/product/components/products-table/products-table.component';
import { SortComponent } from '@b2b/features/product/components/sort/sort.component';
import { ViewConfigComponent } from '@b2b/features/product/components/view-config/view-config.component';
import { ProductListService } from '@b2b/features/product/core/services/product-list.service';
import { TranslateModule } from '@ngx-translate/core';
import { combineLatest, map, take } from 'rxjs';
import { CmsComponentPipe } from '@app/b2b/core/cms/pipes/cms-component.pipe';
import { CxAbstractComponent } from '@b2b/components/cms/cx-abstract/cx-abstract-component/cx-abstract.component';
import { CX_ABSTRACT_SECTION_COMPONENTS } from '@b2b/components/cms/cx-abstract/cx-abstract.tokens';
import { PaginationComponent } from '@shared/components/pagination/pagination.component';
import { ScrollerModule } from 'primeng/scroller';
import { ProductLoadingComponent } from '@b2b/components/product/product-loading/product-loading.component';
import { CxInViewportDirective } from '@app/core/shared/directives/cx-in-viewport';

@Component({
  selector: 'cx-product-list-section',
  templateUrl: './cx-product-list-section.component.html',
  styleUrls: ['./cx-product-list-section.component.scss'],
  imports: [
    CommonModule,
    PaginationComponent,
    ProductCardComponent,
    FacetsComponent,
    SortComponent,
    LocalLoaderComponent,
    ColumnsConfigComponent,
    ViewConfigComponent,
    ProductsTableComponent,
    TranslateModule,
    CxAbstractComponent,
    CmsComponentPipe,
    ScrollerModule,
    ProductLoadingComponent,
    CxInViewportDirective,
  ],
})
export class CxProductListSectionComponent implements OnDestroy {
  protected components = inject<CmsComponent[]>(CX_ABSTRACT_SECTION_COMPONENTS);

  CX_COMPONENT = CX_COMPONENT;

  productList$ = this.productListService.get().pipe(map(data => data.products?.data?.products));
  totalProducts$ = this.productListService.get().pipe(map(data => data.products?.data?.totalProducts || 0));
  isLoading$ = this.productListService.get().pipe(map(data => !!data.products?.isLoading));
  currentPage$ = this.productListService.get().pipe(map(data => +(data.products?.data?.currentPage ?? 0) + 1));
  totalPages$ = this.productListService.get().pipe(map(data => data.products?.data?.totalPages || 0));
  emptyMessage$ = this.productListService.get().pipe(map(data => data.products?.message || ''));

  didYouMean$ = this.productListService.get().pipe(map(data => data.products?.data?.didYouMean));
  categoryMenu = '';
  titleSearch = '';
  gridOption$ = this.productListService.gridOption$;

  visibleFlags: Record<number, boolean> = {
    0: true,
    1: true,
    2: true,
    3: true,
    4: true,
    5: true,
    6: true,
    7: true,
  };

  constructor(
    private productListService: ProductListService,
    private activatedRoute: ActivatedRoute,
    private breadcrumbService: BreadcrumbService,
    private titleService: Title,
    private router: Router,
  ) {
    this.loadProducts(false);
  }

  onItemVisible(visible: boolean, index: number) {
    if (visible) {
      this.visibleFlags[index] = true;
    }
  }

  ngOnDestroy(): void {
    this.productListService.clear();
  }

  selectGridOption(option: string) {
    if (option === 'list') {
      this.loadProducts(true, 10);
    } else {
      this.loadProducts(true, 24);
    }
    this.gridOption$.next(option);
  }

  loadProducts(resetPage: boolean, pageSize?: number) {
    combineLatest([this.activatedRoute.queryParams, this.activatedRoute.params])
      .pipe(take(1))
      .subscribe(([query, params]) => {
        this.loadBreadCrumb(params);

        this.setTitleSearch(params, query);

        const queryParams: string = Object.keys(query)
          .filter(key => key != 'page')
          .map(key => {
            return key != 'query' ? `${key}:${query[key]}` : `query=${query[key]}`;
          })
          .join('&');

        this.fetchProducts(query, queryParams, params, pageSize, resetPage);

        if (this.titleSearch) {
          this.titleService.setTitle(`Busca: ${this.titleSearch}`);
        }
      });
  }

  didYouMeanChangeSearch(didYouMean?: string) {
    const currentQuery = this.activatedRoute.snapshot.queryParams;
    const oldValue: string = currentQuery['query'].split(':')[0];
    const newValue: string = currentQuery['query'].replace(oldValue, didYouMean);

    this.router.navigate([], {
      queryParams: {
        ['query']: newValue,
        ['page']: 1,
      },
      queryParamsHandling: 'merge',
    });
  }

  loadBreadCrumb(params: Params): void {
    const initialValue: Link[] = [];

    const links = Object.entries<string>(params).reduce((acc, param, index) => {
      const [_, path] = param;

      acc.push({
        linkName: path.replaceAll('-', ' '),
        routerLink: [acc?.[index - 1]?.routerLink?.join('/') || '/c', path],
      });

      return acc;
    }, initialValue);

    this.breadcrumbService.setLinks([{ linkName: 'Home', routerLink: [''] }, ...links]);
  }

  setTitleSearch(params: Params, query: Params) {
    if (Object.keys(params).length > 0) {
      const paramsValues = Object.values(params);
      this.categoryMenu = paramsValues.join('/');

      this.titleSearch = query['query']
        ? query['query'].split(':')[0] == '*'
          ? this.categoryMenu
          : query['query'].split(':')[0]
        : this.categoryMenu || '*';
    } else {
      this.titleSearch = query['query'] ? query['query'].split(':')[0] : '*';
    }
  }

  onPageChange(page: number): void {
    combineLatest([this.activatedRoute.queryParams, this.activatedRoute.params])
      .pipe(take(1))
      .subscribe(([query, params]) => {
        this.loadBreadCrumb(params);
        this.setTitleSearch(params, query);

        const queryParams: string = Object.keys(query)
          .filter(key => key != 'page')
          .map(key => {
            return key != 'query' ? `${key}:${query[key]}` : `query=${query[key]}`;
          })
          .join('&');

        this.fetchProducts(query, queryParams, params, undefined, false, page - 1);
      });
  }

  fetchProducts(
    query: Params,
    queryParams: string,
    params: Params,
    pageSize?: number,
    resetPage?: boolean,
    explicitPage?: number,
  ) {
    const limit = pageSize || 24;
    const page = explicitPage !== undefined ? explicitPage : resetPage ? 0 : query['page'] ? +query['page'] - 1 : 0;

    return this.productListService.load({
      limit,
      page,
      query: queryParams || '',
      params: params || '',
      categoryID: this.categoryMenu ? this.categoryMenu.replace(/(^|\s)\S/g, match => match.toUpperCase()) : '',
    });
  }
}
